import * as React from "react";
import type { UIMessage } from "ai";
import { ArrowDownIcon, DownloadIcon } from "lucide-react";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";
import { Button } from "../primitives/button.js";
import { cn } from "../lib/cn.js";

export type ConversationProps = React.ComponentProps<typeof StickToBottom>;

/**
 * AI Elements Conversation, adapted from Vercel's registry component for
 * Flint tokens.
 */
export function Conversation({ className, ...props }: ConversationProps) {
  return (
    <StickToBottom
      className={cn("relative min-h-0 flex-1 overflow-y-hidden", className)}
      initial="smooth"
      resize="smooth"
      role="log"
      {...props}
    />
  );
}

export type ConversationContentProps = React.ComponentProps<typeof StickToBottom.Content>;

export function ConversationContent({ className, ...props }: ConversationContentProps) {
  return <StickToBottom.Content className={cn("flex flex-col gap-6 p-4", className)} {...props} />;
}

export interface ConversationEmptyStateProps extends React.ComponentProps<"div"> {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
}

export function ConversationEmptyState({
  className,
  title = "No messages yet",
  description = "Start a conversation to see messages here",
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) {
  return (
    <div
      className={cn(
        "flex size-full flex-col items-center justify-center gap-3 p-8 text-center",
        className,
      )}
      {...props}
    >
      {children ?? (
        <>
          {icon ? <div className="text-muted">{icon}</div> : null}
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-ink">{title}</h3>
            {description ? <p className="text-sm text-muted">{description}</p> : null}
          </div>
        </>
      )}
    </div>
  );
}

export type ConversationScrollButtonProps = React.ComponentProps<typeof Button>;

export function ConversationScrollButton({ className, ...props }: ConversationScrollButtonProps) {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();
  if (isAtBottom) return null;

  return (
    <Button
      className={cn(
        "absolute bottom-4 left-1/2 h-9 w-9 -translate-x-1/2 rounded-full p-0 shadow-card",
        className,
      )}
      intent="ghost"
      onClick={() => scrollToBottom()}
      type="button"
      {...props}
    >
      <ArrowDownIcon className="h-4 w-4" aria-hidden />
      <span className="sr-only">Scroll to bottom</span>
    </Button>
  );
}

export interface ConversationDownloadProps extends Omit<
  React.ComponentProps<typeof Button>,
  "onClick"
> {
  messages: UIMessage[];
  filename?: string;
  formatMessage?: (message: UIMessage, index: number) => string;
}

export function ConversationDownload({
  messages,
  filename = "conversation.md",
  formatMessage = defaultFormatMessage,
  className,
  children,
  ...props
}: ConversationDownloadProps) {
  const handleDownload = React.useCallback(() => {
    const markdown = messages.map((message, index) => formatMessage(message, index)).join("\n\n");
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [messages, filename, formatMessage]);

  return (
    <Button
      className={cn("h-9 w-9 rounded-full p-0", className)}
      intent="ghost"
      onClick={handleDownload}
      type="button"
      {...props}
    >
      {children ?? <DownloadIcon className="h-4 w-4" aria-hidden />}
      <span className="sr-only">Download conversation</span>
    </Button>
  );
}

function defaultFormatMessage(message: UIMessage) {
  const roleLabel = message.role.charAt(0).toUpperCase() + message.role.slice(1);
  const text = message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("");
  return `**${roleLabel}:** ${text}`;
}
